import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { WizardLayout } from '@reflex/ui/components/layout/WizardLayout';
import { useAuth } from '@/hooks/auth/useAuth';
import { ROUTES } from '@/lib/routes';
import { orgSetupApi } from '@/lib/org-setup-api';
import { WizardStepper } from '@/components/setup/WizardStepper';
import { WizardOrgSwitcher } from '@/components/setup/WizardOrgSwitcher';
import {
  ALL_STAGES,
  EXISTING_DEFAULT_STAGE,
  isOrgSetupStage,
  LAST_STAGE,
  ORG_SETUP_STAGES,
  type OrgSetupStage,
} from './wizardStages';
import { OrganizationStage } from './stages/OrganizationStage';
import { OrganizationDetailsStage } from './stages/OrganizationDetailsStage';
import { RunloopAccountStage } from './stages/RunloopAccountStage';
import { DoneStage } from './stages/DoneStage';

/**
 * Phase 2 (per-org) setup wizard. Mounts in two modes:
 *
 * - `/org-setup/new` — wizard begins on the `organization` stage and
 *   creates the org via the normal authed `POST /organizations`. Once
 *   created, navigates to `/orgs/:orgSlug/setup/runloop` to finish the
 *   rest of the wizard.
 * - `/orgs/:orgSlug/setup/:stage` — wizard renders the URL-selected stage,
 *   scoped to the org resolved from the slug. A bare `/orgs/:orgSlug/setup`
 *   redirects to `/orgs/:orgSlug/setup/runloop` (see `App.tsx`). The
 *   `:stage` segment makes wizard progress deep-linkable: refreshing
 *   the page or sharing the URL keeps you on the same step, and
 *   browser back/forward navigates between stages.
 *
 * The stages themselves live in `./stages/*`; this component owns only
 * stage selection (which stages exist for this org, which one the URL
 * points at, and how the wizard advances).
 *
 * Both modes always render the same 3-stage rail so the counter
 * progresses naturally across the redirect: a user who finishes the
 * `organization` stage lands on `/orgs/:orgSlug/setup/runloop` and sees
 * "Step 2 of 3" with step 1 already checked, instead of the counter
 * resetting to "Step 1 of 2". In existing mode the `organization` step
 * renders an edit form (see `OrganizationDetailsStage`) so stepping back
 * lets the user fix the name or support email — it must never re-mount
 * the create-org form, which would either fail with a slug collision or
 * silently spawn a duplicate org.
 */
export function OrgSetupPage({ mode }: { mode: 'new' | 'existing' }) {
  const navigate = useNavigate();
  const params = useParams<{ orgSlug?: string; stage?: string }>();
  const auth = useAuth();
  const queryClient = useQueryClient();

  const resolvedOrg = useMemo(() => {
    if (mode !== 'existing' || !params.orgSlug) return null;
    return (
      auth.memberships.find((m) => m.organization.slug === params.orgSlug)?.organization ?? null
    );
  }, [auth.memberships, mode, params.orgSlug]);

  // Per-org setup state drives the wizard's one optional stage: `runloop`
  // shows until the org has a sandbox key (`sandboxConfigured`), so an org
  // that transferred one at create time skips it. Gated to existing mode
  // (new mode has no org yet) and never cached so a fresh read reflects a
  // key written moments earlier.
  const stateQuery = useQuery({
    queryKey: ['org-setup', resolvedOrg?.id, 'state'] as const,
    queryFn: () => orgSetupApi.getState(resolvedOrg!.id),
    enabled: mode === 'existing' && !!resolvedOrg,
  });
  const setupState = stateQuery.data;

  const visibleStages = useMemo(() => {
    // Keep `runloop` visible until the state proves the sandbox key is
    // already there (e.g. transferred at create time), so the rail never
    // flashes a step away and back while the state loads.
    const showRunloop = !setupState || !setupState.sandboxConfigured;
    return ALL_STAGES.filter((s) => (s.id === 'runloop' ? showRunloop : true));
  }, [setupState]);
  const visibleStageIds = useMemo(() => visibleStages.map((s) => s.id), [visibleStages]);

  // Stage source of truth:
  //   - `mode="new"` only renders the `organization` stage — no URL
  //     segment is needed because there is nothing to navigate between
  //     (org creation immediately hands off to existing mode).
  //   - `mode="existing"` reads `:stage` from the URL so refresh /
  //     deep-link / back-forward all preserve wizard progress.
  // A malformed `:stage` (e.g. an old bookmark) silently snaps back to
  // the existing-mode default; we do the swap in `useEffect` so the URL
  // stays authoritative and `setStage` always navigates rather than
  // forking the state into a local copy.
  const urlStage = params.stage;
  const stage: OrgSetupStage =
    mode === 'new' ? 'organization' : isOrgSetupStage(urlStage) ? urlStage : EXISTING_DEFAULT_STAGE;

  // The stage actually rendered. When the URL points at a stage the org
  // skipped (`runloop` after a key transfer) we clamp to the first
  // remaining visible stage. Clamping (rather than blocking on the state load) keeps the rail's
  // "current" dot always inside `visibleStages` and avoids a loading flash;
  // the effect below then rewrites the URL to match.
  const effectiveStage = useMemo<OrgSetupStage>(() => {
    if (visibleStageIds.includes(stage)) return stage;
    const start = ORG_SETUP_STAGES.indexOf(stage);
    for (let i = start; i < ORG_SETUP_STAGES.length; i++) {
      if (visibleStageIds.includes(ORG_SETUP_STAGES[i])) return ORG_SETUP_STAGES[i];
    }
    for (let i = start - 1; i >= 0; i--) {
      if (visibleStageIds.includes(ORG_SETUP_STAGES[i])) return ORG_SETUP_STAGES[i];
    }
    return LAST_STAGE;
  }, [stage, visibleStageIds]);

  useEffect(() => {
    if (mode !== 'existing' || !params.orgSlug) return;
    if (urlStage && !isOrgSetupStage(urlStage)) {
      navigate(ROUTES.org(params.orgSlug).setupStage(EXISTING_DEFAULT_STAGE), { replace: true });
    }
  }, [mode, navigate, params.orgSlug, urlStage]);

  const setStage = useCallback(
    (next: OrgSetupStage, opts?: { replace?: boolean }) => {
      if (mode === 'new' || !params.orgSlug) return;
      navigate(
        ROUTES.org(params.orgSlug).setupStage(next),
        opts?.replace ? { replace: true } : undefined,
      );
    },
    [mode, navigate, params.orgSlug],
  );

  // Advance to the next *visible* stage so skipped stages never trap the
  // linear flow (e.g. runloop → done when the sandbox key was transferred
  // at create time).
  const goToNext = useCallback(
    (from: OrgSetupStage) => {
      const idx = visibleStageIds.indexOf(from);
      setStage(visibleStageIds[idx + 1] ?? LAST_STAGE);
    },
    [visibleStageIds, setStage],
  );

  // Keep the URL honest once we know which stages exist: if it points at a
  // skipped stage, rewrite it to the clamped stage we're already rendering.
  // Gated on the setup state being loaded so we never rewrite `runloop` away
  // before we know whether the org still needs it.
  useEffect(() => {
    if (mode !== 'existing' || !params.orgSlug || !setupState) return;
    if (effectiveStage !== stage) {
      setStage(effectiveStage, { replace: true });
    }
  }, [mode, params.orgSlug, setupState, stage, effectiveStage, setStage]);

  // Self-serve creation is off platform-wide (and the user is not a platform
  // admin, who keeps the server-side bypass). Say so here instead of
  // rendering a form the server answers with 403 — this route is reachable
  // directly by URL, from the welcome screen, and from the org switcher.
  if (mode === 'new' && !auth.canCreateOrganization) {
    return (
      <WizardLayout centered>
        <Alert className="max-w-md" data-testid="org-setup-creation-disabled">
          <AlertTitle>Organization creation is turned off</AlertTitle>
          <AlertDescription>
            An administrator has turned off self-serve organization creation. Ask an administrator
            to create an organization for you, or to send you an invite to an existing one.
          </AlertDescription>
        </Alert>
      </WizardLayout>
    );
  }

  if (mode === 'existing' && params.orgSlug && resolvedOrg == null) {
    return (
      <WizardLayout centered>
        <Alert variant="destructive" className="max-w-md">
          <AlertTitle>Organization not found</AlertTitle>
          <AlertDescription>
            You don't appear to have access to <code>{params.orgSlug}</code>.
          </AlertDescription>
        </Alert>
      </WizardLayout>
    );
  }

  return (
    <WizardLayout
      eyebrow="Organization setup"
      // A member of several orgs must be able to leave this wizard: the
      // gate routes them here whenever the *active* org needs setup, and
      // without a switcher the only way out of another org's bootstrap
      // (or of `/org-setup/new`) is hand-editing the URL. Renders itself
      // only when the user has somewhere else to go.
      headerSlot={
        <WizardOrgSwitcher currentOrgSlug={mode === 'existing' ? params.orgSlug : null} />
      }
    >
      <WizardStepper
        stages={visibleStages}
        current={effectiveStage}
        furthest={effectiveStage}
        onSelect={setStage}
      />

      {effectiveStage === 'organization' && mode === 'new' && (
        <OrganizationStage
          onCreated={async (slug) => {
            // Await both refreshes BEFORE navigating: the destination
            // route (`/orgs/:orgSlug/setup/:stage`) immediately reads
            // `auth.memberships` to resolve the org from the URL slug,
            // so if we navigate while memberships are stale the
            // wizard briefly renders an "Organization not found"
            // alert before the auth state catches up.
            await auth.refreshSession();
            await queryClient.invalidateQueries();
            // Land on the Runloop step; if a key transfer already
            // satisfied it, the clamp + redirect below resolve to the
            // first stage the org still needs.
            navigate(ROUTES.org(slug).setupStage('runloop'), { replace: true });
          }}
        />
      )}
      {effectiveStage === 'organization' && mode === 'existing' && resolvedOrg && (
        <OrganizationDetailsStage org={resolvedOrg} onContinue={() => goToNext('organization')} />
      )}
      {effectiveStage === 'runloop' && resolvedOrg && (
        <RunloopAccountStage orgId={resolvedOrg.id} onNext={() => goToNext('runloop')} />
      )}
      {effectiveStage === 'done' && resolvedOrg && (
        <DoneStage orgId={resolvedOrg.id} orgSlug={resolvedOrg.slug} />
      )}
    </WizardLayout>
  );
}
